"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useLocaleRouter } from "@/lib/hooks/use-locale-router";
import { useTranslations } from "next-intl";
import { X, ArrowRight, CaretDown } from "@phosphor-icons/react";
import SearchAutocomplete, { type SearchAutocompleteHandle } from "@/components/ui/SearchAutocomplete";
import { getCategoryLabel, type DirectoryCategory, DIRECTORY_CATEGORIES } from "@/lib/categories";
import { LOCATION_SLUGS, type LocationSlug } from "@/lib/provinces";
import { stripLocalePrefix } from "@/i18n/routing";
import { canonicalLanguageLabel, langLabelStandalone, LANG_LABEL_TO_CODE } from "@/lib/lang-utils";
import { useLocale } from "@/lib/hooks/use-locale";
import ProvinceFilter from "@/components/ui/ProvinceFilter";
import { useFocusTrap } from "@/lib/hooks/use-focus-trap";
import { SEARCH_COMPOSER_OPEN_EVENT } from "@/lib/events";
import { acquireBodyScrollLock, releaseBodyScrollLock } from "@/lib/body-scroll-lock";
import type { SuggestionCounts, SpecialtySuggestion } from "@/lib/queries/professionals";

const LANGUAGES = ["English", "Vietnamese", "French", "Japanese", "Korean", "Mandarin", "Cantonese", "Tagalog"];

interface SearchComposerProps {
  counts?: SuggestionCounts;
  specialties?: SpecialtySuggestion[];
}

function FilterRow({
  name,
  value,
  current,
  label,
  onChange,
}: {
  name: string;
  value: string;
  current: string;
  label: string;
  onChange: (val: string) => void;
}) {
  const active = current === value;
  return (
    <li>
      <label className="flex min-h-[44px] cursor-pointer items-center gap-13 text-sm text-gray-600 hover:text-navy select-none">
        <input
          type="radio"
          name={name}
          value={value}
          checked={active}
          onChange={() => onChange(value)}
          className="sr-only"
        />
        <span
          className={`flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-full border transition-colors ${
            active ? "border-navy" : "border-navy/25 bg-white"
          }`}
          aria-hidden="true"
        >
          {active && <span className="h-[8px] w-[8px] rounded-full bg-navy" />}
        </span>
        {label}
      </label>
    </li>
  );
}

/**
 * Universal mobile-only search composer. Opened from anywhere via
 * `window.dispatchEvent(new CustomEvent(SEARCH_COMPOSER_OPEN_EVENT))` — today
 * only from the /search "Filters" button, the bottom bar's Search tab having
 * been retired with the bar itself (2026-09-05). Always operates in buffered mode — radios/text don't apply
 * until the user taps "Show results" or selects an autocomplete
 * suggestion (which has its own href and bypasses the buffer).
 */
export default function SearchComposer({ counts = {}, specialties = [] }: SearchComposerProps) {
  const router = useLocaleRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const t = useTranslations("search");
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<SearchAutocompleteHandle>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  // Mirror `open` and `initFromUrl` in refs so the global event listener
  // can read the latest values without re-binding on every change.
  // Re-binding on URL changes / open toggles risks dropping events that
  // fire during the cleanup→bind window.
  const openRef = useRef(false);
  const handleTrapKeyDown = useFocusTrap(drawerRef, open);

  // Buffered filter state — initialized from URL on each open
  const [bufferedQ, setBufferedQ] = useState("");
  const [bufferedCategory, setBufferedCategory] = useState("");
  const [bufferedLocation, setBufferedLocation] = useState("");
  const [bufferedLanguage, setBufferedLanguage] = useState("");

  // Match both `/search` and `/search/` to be defensive against any
  // trailing-slash variant Next might emit. Strip the locale prefix first so
  // `/vi/search` and `/fr/search` are recognised, not just the EN URL.
  const localePath = stripLocalePrefix(pathname);
  const onSearchPage = localePath === "/search" || localePath === "/search/";

  const initFromUrl = useCallback(() => {
    // useSearchParams() is typed `ReadonlyURLSearchParams | null` —
    // null during static prerender. The marketing layout is
    // force-dynamic so this is unreachable today, but guard so the
    // composer doesn't crash if that ever changes.
    // Keep the ref in sync for the listener below.
    let nextQ = "";
    if (onSearchPage && searchParams) {
      nextQ = searchParams.get("q") ?? "";
      setBufferedQ(nextQ);
      setBufferedCategory(searchParams.get("category") ?? "");
      // Coerce stale label URLs (`?location=Hanoi`) to "" so the radio
      // group falls back to "All Locations" instead of leaving every
      // option unselected. Mirrors the same guard on /search/page.tsx.
      const rawLocation = searchParams.get("location") ?? "";
      const validLocation = (LOCATION_SLUGS as readonly string[]).includes(rawLocation) ? rawLocation : "";
      setBufferedLocation(validLocation);
      setBufferedLanguage(canonicalLanguageLabel(searchParams.get("language") ?? ""));
    } else {
      setBufferedQ("");
      setBufferedCategory("");
      setBufferedLocation("");
      setBufferedLanguage("");
    }
    // Push the new q into the autocomplete imperatively. The component
    // is uncontrolled (`defaultValue` only seeds initial state), so a
    // bufferedQ change wouldn't otherwise propagate to the input now
    // that the composer is always-mounted for transitions.
    searchRef.current?.setValue(nextQ);
  }, [onSearchPage, searchParams]);

  // Keep openRef in sync with open state
  useEffect(() => { openRef.current = open; }, [open]);

  // Listen for global open event. Bind once on mount; reads `open` and
  // `initFromUrl` via refs so we never miss a tap that lands during a
  // listener-rebind window after a state/URL change.
  const initFromUrlRef = useRef(initFromUrl);
  useEffect(() => { initFromUrlRef.current = initFromUrl; }, [initFromUrl]);

  useEffect(() => {
    const handler = (e: Event) => {
      // Re-tap idiom: if already open, no-op. We still preventDefault
      // below so the dispatcher (today `SearchFilters`) suppresses any
      // fallback navigation — re-tap should not re-route to /search either.
      if (e.cancelable) e.preventDefault();
      if (openRef.current) return;
      // Capture the trigger so we can restore focus on close.
      const active = document.activeElement;
      restoreFocusRef.current = active instanceof HTMLElement ? active : null;
      initFromUrlRef.current();
      // A dispatcher may carry a query the reader typed somewhere else — today
      // the docked mini search (`mini-search-fallback-spec.md` §4). `initFromUrl`
      // seeds from the URL, so without this the text they just typed is silently
      // discarded on the way into the drawer. Read defensively: `detail` is
      // whatever the dispatcher put there, and anyone can dispatch this event.
      const q = (e as CustomEvent<{ q?: unknown } | null>).detail?.q;
      if (typeof q === "string" && q.trim()) {
        setBufferedQ(q);
        searchRef.current?.setValue(q);
      }
      setOpen(true);
    };
    window.addEventListener(SEARCH_COMPOSER_OPEN_EVENT, handler);
    return () => window.removeEventListener(SEARCH_COMPOSER_OPEN_EVENT, handler);
  }, []);

  // Restore focus to the trigger when the drawer closes
  useEffect(() => {
    if (open) return;
    const el = restoreFocusRef.current;
    if (!el || !document.body.contains(el)) return;
    // Run after the next paint so the closing transition doesn't steal focus
    const t = requestAnimationFrame(() => el.focus());
    return () => cancelAnimationFrame(t);
  }, [open]);

  // Lock body scroll while open. Refcounted via the shared lock manager so a
  // concurrent overlay's cleanup can't wipe ours (and vice versa).
  useEffect(() => {
    if (!open) return;
    acquireBodyScrollLock();
    return () => releaseBodyScrollLock();
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      // An Escape the autocomplete already handled closed only its list.
      if (e.key === "Escape" && !e.defaultPrevented) setOpen(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  // Auto-focus the search input on open. Two rAF ticks puts the focus
  // call after the drawer's entrance frame so iOS Safari reliably
  // surfaces the keyboard. More reliable than a fixed setTimeout.
  useEffect(() => {
    if (!open) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => searchRef.current?.focus());
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [open]);

  const close = () => setOpen(false);

  // Any filter actively buffered? Drives the title↔"Clear all" swap in the
  // header. Includes the free-text query because clearing only the radio
  // groups when a query is set would leave the user staring at half-cleared
  // state.
  const hasBufferedFilters = !!(
    bufferedQ || bufferedCategory || bufferedLocation || bufferedLanguage
  );

  const clearAll = () => {
    setBufferedQ("");
    setBufferedCategory("");
    setBufferedLocation("");
    setBufferedLanguage("");
    // The autocomplete is uncontrolled (seeded via defaultValue), so the
    // setBufferedQ above doesn't push back into its visible input. Use
    // its imperative handle to wipe what the user typed.
    searchRef.current?.clear();
  };

  /**
   * Land at the TOP of the page after a submit. Defers via rAF so the body
   * scroll-lock release + close transition commit first.
   *
   * Unconditional, with no breakpoint check, because this drawer is
   * `md:hidden` — it only exists below 768px, so there is no desktop case to
   * preserve. Two earlier versions got this wrong:
   *
   *  - scrolling the results section into view instead resolved that element
   *    against the PRE-navigation DOM (the rAF fires before the new page
   *    renders). Submitting from the homepage found no results section, bailed
   *    out, and left `scroll: false` to carry the old offset onto /search —
   *    landing the visitor ~1300px down a page they had just opened.
   *  - branching on the `sm` mirror (640px) left the 640–767px band, where the
   *    drawer is still fully usable, taking the wrong path.
   *
   * Must also run when submit SKIPS the navigation (same params, already on
   * /search): there the scroll is the only feedback that the tap registered.
   */
  const scrollAfterSubmit = () => {
    requestAnimationFrame(() => {
      const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({ top: 0, behavior: prefersReduced ? "auto" : "smooth" });
    });
  };

  const submit = (overrides?: {
    q?: string;
    category?: string;
    location?: string;
    language?: string;
  }) => {
    const params = new URLSearchParams();
    const values = {
      q: overrides?.q ?? bufferedQ,
      category: overrides?.category ?? bufferedCategory,
      location: overrides?.location ?? bufferedLocation,
      language: overrides?.language ?? bufferedLanguage,
    };
    for (const [k, v] of Object.entries(values)) {
      if (v) params.set(k, v);
    }
    const qs = params.toString();
    const target = qs ? `/search?${qs}` : "/search";
    // Skip the navigation if the user submitted the same params they
    // arrived with (e.g. opened on /search, hit Show Results without
    // changing anything). Saves an RSC roundtrip.
    const current = onSearchPage && searchParams ? searchParams.toString() : "";
    const sameParams = qs === current && onSearchPage;
    if (!sameParams) {
      // `scroll: false` suppresses Next's default scroll-to-top so we
      // can land precisely on the results section (skipping the
      // breadcrumb / heading on desktop).
      router.push(target, { scroll: false });
    }
    close();
    scrollAfterSubmit();
  };

  const onAutocompleteSelect = ({
    intent,
    href,
    query,
  }: {
    intent: "suggestion" | "submit";
    href: string | null;
    query: string;
  }) => {
    if (intent === "suggestion" && href) {
      // Suggestion is composite (may include category/location). Bypass
      // buffer and navigate directly — overrides any buffered radios.
      router.push(href);
      close();
      return;
    }
    // Free-text submit (Enter on input). Apply buffered filters with this q.
    submit({ q: query });
  };

  // Note: we keep the component mounted (no `if (!open) return null`) so the
  // backdrop fade + panel slide can animate on close. `pointer-events-none`
  // on the wrapper means the (transparent) backdrop and (off-screen) panel
  // don't intercept clicks while transitioning out. Side effects (scroll
  // lock, autofocus, ESC handler) gate on `open` internally.
  return (
    <div
      className={`md:hidden ${open ? "" : "pointer-events-none"}`}
      aria-hidden={!open}
    >
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-overlay bg-black/40 transition-opacity duration-300 ease-out ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={close}
        aria-hidden="true"
      />
      {/* Panel — translateY for cheap GPU-composited transition.
          cubic-bezier(0.32, 0.72, 0, 1) is a "spring-like" curve (Apple's
          sheet ease) that decelerates more naturally than ease-out alone. */}
      <div
        ref={drawerRef}
        role="dialog"
        aria-modal={open ? "true" : undefined}
        aria-label={t("composerTitle")}
        onKeyDown={handleTrapKeyDown}
        className={`fixed bottom-0 left-0 right-0 z-overlay flex h-[97dvh] flex-col rounded-t-pop bg-white shadow-e5 transition-transform duration-[320ms] ease-[cubic-bezier(0.32,0.72,0,1)] ${
          open ? "translate-y-0" : "translate-y-full"
        }`}
      >
        {/*
          Header — title slot crossfades to a "Clear all" action whenever
          any filter is buffered. Both elements are kept mounted so the
          opacity transition is smooth in both directions; the inactive
          one is `pointer-events-none` + `aria-hidden` so it can't be
          tabbed to or read out.
        */}
        <div className="flex shrink-0 items-center justify-between gap-13 border-b border-navy/10 px-21 py-5">
          <div className="relative flex min-h-[44px] flex-1 items-center">
            <span
              aria-hidden={hasBufferedFilters}
              className={`absolute inset-y-0 left-0 flex items-center text-[11px] font-semibold uppercase tracking-widest text-navy transition-opacity duration-200 ${
                hasBufferedFilters ? "opacity-0" : "opacity-100"
              }`}
            >
              {t("composerTitle")}
            </span>
            <button
              type="button"
              onClick={clearAll}
              tabIndex={hasBufferedFilters ? 0 : -1}
              aria-hidden={!hasBufferedFilters}
              aria-label={t("clearAllFilters")}
              className={`group absolute inset-y-0 left-0 flex items-center gap-5 rounded-control text-[11px] font-semibold uppercase tracking-widest text-navy/80 transition-all duration-200 hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold ${
                hasBufferedFilters ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
              }`}
            >
              <X
                size={11}
                weight="bold"
                aria-hidden="true"
                className="transition-transform duration-150 group-hover:rotate-90"
              />
              {t("clearAll")}
            </button>
          </div>
          <button
            onClick={close}
            aria-label={t("close")}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-navy/50 hover:bg-navy/5 hover:text-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold"
          >
            <CaretDown className="h-[14px] w-[14px]" weight="bold" aria-hidden="true" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-21">
          <div className="space-y-34">
            {/* Search */}
            <div>
              <label
                htmlFor="composer-search"
                className="mb-8 block text-[11px] font-semibold uppercase tracking-widest text-navy"
              >
                {t("searchLabel")}
              </label>
              <SearchAutocomplete
                ref={searchRef}
                id="composer-search"
                counts={counts}
                specialties={specialties}
                defaultValue={bufferedQ}
                onValueChange={setBufferedQ}
                onSelect={onAutocompleteSelect}
                placeholder={t("searchPlaceholder")}
              />
            </div>

            {/* Category */}
            <fieldset>
              <legend className="mb-8 text-[11px] font-semibold uppercase tracking-widest text-navy">
                {t("categoryLegend")}
              </legend>
              <ul role="list" className="space-y-5">
                <FilterRow
                  name="category"
                  value=""
                  current={bufferedCategory}
                  label={t("allCategories")}
                  onChange={(v) => setBufferedCategory(v)}
                />
                {DIRECTORY_CATEGORIES.map((cat) => (
                  <FilterRow
                    key={cat}
                    name="category"
                    value={cat}
                    current={bufferedCategory}
                    label={getCategoryLabel(cat as DirectoryCategory, locale) ?? cat}
                    onChange={(v) => setBufferedCategory(v)}
                  />
                ))}
              </ul>
            </fieldset>

            {/* Location — searchable across all 34 provinces + ward
                suggestions. Buffered: writes to `bufferedLocation`; real
                URL navigation happens on the "Show Results" CTA below. */}
            <fieldset>
              <legend className="mb-8 text-[11px] font-semibold uppercase tracking-widest text-navy">
                {t("provinceLegend")}
              </legend>
              <ProvinceFilter
                value={bufferedLocation as LocationSlug}
                onChange={(v) => setBufferedLocation(v)}
                groupLabel={t("filterByProvince")}
                touchTargets
              />
            </fieldset>

            {/* Language */}
            <fieldset>
              <legend className="mb-8 text-[11px] font-semibold uppercase tracking-widest text-navy">
                {t("languageLegend")}
              </legend>
              <ul role="list" className="space-y-5">
                <FilterRow
                  name="language"
                  value=""
                  current={bufferedLanguage}
                  label={t("allLanguages")}
                  onChange={(v) => setBufferedLanguage(v)}
                />
                {LANGUAGES.map((lang) => {
                  // Filter value stays the English name (the DB filter value);
                  // only the display label localizes via langLabelStandalone(code, locale).
                  const code = LANG_LABEL_TO_CODE[lang.toLowerCase()];
                  return (
                    <FilterRow
                      key={lang}
                      name="language"
                      value={lang}
                      current={bufferedLanguage}
                      label={code ? langLabelStandalone(code, locale) : lang}
                      onChange={(v) => setBufferedLanguage(v)}
                    />
                  );
                })}
              </ul>
            </fieldset>
          </div>
        </div>

        {/* Sticky footer — fixed bottom of the flex column, respects safe area */}
        <div
          className="shrink-0 border-t border-navy/10 bg-white px-21 py-13"
          style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 13px)" }}
        >
          <button
            onClick={() => submit()}
            className="relative w-full rounded-control bg-navy py-13 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-navy/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
          >
            {hasBufferedFilters ? t("showResults") : t("seeAllProfessionals")}
            <ArrowRight
              className="absolute right-13 top-1/2 h-[14px] w-[14px] -translate-y-1/2"
              weight="bold"
              aria-hidden="true"
            />
          </button>
        </div>
      </div>
    </div>
  );
}
